import React, { useEffect, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import { CommodityOtcPriceModel, Currency, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import { toOtcSubRegion } from '../../../Apis/Hooks/useOtcPricesApiHook';
import PageLoadingSpinner from '../../../Components/LoadingSpinner/PageLoadingSpinner';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { CommodityOtcPriceCompositeModel } from '../../Futures/Otc/OtcPricesTableDefinitions';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';
import { IDashboardPricesTableProps } from '../DashboardTables/DashboardTableFromPageElement';

const withOtcPricesTableFromPageElement = (WrappedComponent: React.ComponentType<IDashboardPricesTableProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const [translations] = useLanguage();
        const [otcPriceRows, setOtcPriceRows] = useState<CommodityOtcPriceCompositeModel[]>([]);
        const [hasApiResponded, setHasApiResponded] = useState(false);
        const { commodity, exchange, region, currency, unitOfMeasure, commodityDisplayName, subCommodityDisplayName } = props.pageElement;
        const subRegion = toOtcSubRegion(props.pageElement.subRegion);
        const cacheKey = `${CacheKeys.ListCommodityOtcPricesCurrent}_${region}_${commodity}_${subRegion}_${currency}_${unitOfMeasure}_dashboard`;
        const [, , apiResponse] = useCacheThenApi(cacheKey, () =>
            demeterApi.listCommodityOtcPricesCurrent(
                region as string,
                commodity as string,
                subRegion,
                currency ?? Currency.Eur,
                unitOfMeasure ?? UnitOfMeasure.MetricTon,
            ),
        );

        useEffect(() => {
            if (!apiResponse) {
                return;
            }
            setHasApiResponded(true);
            if (apiResponse.rows && apiResponse.rows.length > 0) {
                const rows = apiResponse.rows.map((row: CommodityOtcPriceModel, index: number) => ({
                    ...row,
                    id: `${index}`,
                    rowIndex: index,
                    selected: false,
                }));
                setOtcPriceRows(rows);
            }
        }, [apiResponse]);

        const commodityPart = (subCommodityDisplayName ?? commodityDisplayName ?? '') as string;
        const exchangeSuffix = (exchange && translations.exchange[exchange as string]) || 'OTC';
        const title = [commodityPart, exchangeSuffix].filter(Boolean).join(' ');

        if (!hasApiResponded) {
            return <PageLoadingSpinner />;
        }

        const customTitle = typeof props.customTitle === 'string' ? props.customTitle : undefined;

        return (
            <WrappedComponent
                tableType="otc"
                tableData={otcPriceRows}
                title={title}
                customTitle={customTitle}
                dragIndicatorIcon={props.dragIndicatorIcon}
                isInDashboardEditMode={props.isInDashboardEditMode}
                maximizeMinimizeButton={props.maximizeMinimizeButton}
                useDynamicHeight={props.useDynamicHeight}
                handleMoreOptionsSelection={props.handleMoreOptionsSelection}
            />
        );
    };

    return Wrapped;
};

export default withOtcPricesTableFromPageElement;
